import React, { useState, useEffect, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import styled from 'styled-components';
import { v4 as uuidv4 } from 'uuid';

const Container = styled.div`
  position: relative;
  width: 100%;
  height: 100vh;
  overflow: hidden;
  background-color: #f9f7f2;
  display: flex;
  flex-direction: column;
  align-items: center;
  font-family: 'Cormorant Garamond', serif;
`;

const Ground = styled.div`
  position: absolute;
  bottom: 0;
  width: 100%;
  height: 240px;
  background-color: #e6dfd1;
  display: flex;
  flex-direction: column;
  justify-content: flex-start;
  align-items: center;
  color: #3a3a3a;
  font-size: 1.3rem;
  text-align: center;
  padding: 0;
  white-space: pre-line;
  font-family: 'Cormorant Garamond', serif;
  box-shadow: 0 -8px 30px rgba(0, 0, 0, 0.05);
  
  @media (max-width: 768px) {
    height: 200px;
    font-size: 1.1rem;
    bottom: 0;
  }
`;

const GroundText = styled.div`
  padding: 15px 20px;
  padding-top: 30px;
  position: relative;
  z-index: 1;
  font-family: 'Cormorant Garamond', serif;
  font-style: normal;
  line-height: 1.5;
  
  @media (max-width: 768px) {
    padding: 8px 15px;
    font-size: 1rem;
  }
`;

const Seed = styled(motion.div)`
  position: relative;
  width: 120px;
  height: 120px;
  background-color: #766a5d;
  border-radius: 50%;
  display: flex;
  justify-content: center;
  align-items: center;
  color: #f2efea;
  font-size: 0.9rem;
  line-height: 1.4;
  text-align: center;
  padding: 15px;
  cursor: pointer;
  z-index: 1;
  box-shadow: 0 6px 20px rgba(118, 106, 93, 0.4);
  margin-top: 20px;
  margin-left: auto;
  margin-right: auto;
  font-family: 'Cormorant Garamond', serif;
  letter-spacing: 0.5px;
  
  @media (max-width: 768px) {
    width: 100px;
    height: 100px;
    font-size: 0.8rem;
    padding: 10px;
    margin-top: 10px;
  }
`;

const EncourageButton = styled.button`
  position: absolute;
  top: 20px;
  right: 20px;
  padding: 12px 15px;
  background-color: transparent;
  color: #3a3a3a;
  border: 1px solid #3a3a3a;
  border-radius: 0;
  cursor: pointer;
  font-size: 0.8rem;
  font-family: 'Cormorant Garamond', serif;
  font-weight: 400;
  letter-spacing: 3px;
  text-transform: uppercase;
  transition: all 0.3s ease;
  z-index: 10;
  position: relative;
  overflow: hidden;
  
  &:before {
    content: '';
    position: absolute;
    bottom: 0;
    left: 0;
    width: 0;
    height: 1px;
    background-color: #3a3a3a;
    transition: width 0.5s ease;
  }
  
  &:hover {
    &:before {
      width: 100%;
    }
  }
  
  &:after {
    content: '↓';
    margin-left: 10px;
    font-size: 0.9rem;
  }
  
  @media (max-width: 768px) {
    top: 10px;
    right: 10px;
    padding: 8px 12px;
    font-size: 0.7rem;
  }
`;

const Stem = styled(motion.div)`
  position: absolute;
  bottom: 210px;
  left: 50%;
  transform: translateX(-50%);
  width: 3px;
  height: 0;
  background-color: #9b8b7a;
  
  @media (max-width: 768px) {
    bottom: 200px;
    width: 2px;
  }
`;

const StemWord = styled(motion.div)`
  position: absolute;
  color: #5a5245;
  font-size: 1.3rem;
  font-weight: 400;
  font-style: normal;
  text-align: center;
  white-space: nowrap;
  left: 50%;
  transform: translateX(-50%);
  font-family: 'Cormorant Garamond', serif;
  letter-spacing: 1px;
  
  @media (max-width: 768px) {
    font-size: 1.1rem;
  }
`;

const Blossom = styled(motion.div)`
  position: absolute;
  width: 120px;
  height: 120px;
  background: radial-gradient(circle, #e0d4e4 0%, #c9b1c9 100%);
  border-radius: 50%;
  display: flex;
  justify-content: center;
  align-items: center;
  color: #3a3a3a;
  font-size: 1.1rem;
  text-align: center;
  padding: 10px;
  z-index: 3;
  font-family: 'Cormorant Garamond', serif;
  box-shadow: 0 6px 25px rgba(161, 140, 171, 0.15);
  font-style: normal;
  letter-spacing: 1px;
  
  @media (max-width: 768px) {
    width: 100px;
    height: 100px;
    font-size: 1rem;
  }
`;

const BlossomContainer = styled.div`
  position: absolute;
  top: ${props => props.topPosition ? `${props.topPosition}px` : '0px'};
  left: 50%;
  transform: translateX(-50%);
  display: flex;
  justify-content: center;
  align-items: center;
`;

const Petal = styled(motion.div)`
  position: absolute;
  background: linear-gradient(135deg, #e0d4e4 0%, #c9b1c9 100%);
  padding: 12px;
  border-radius: 50%;
  color: #4a3e54;
  font-size: 0.85rem;
  text-align: center;
  max-width: 100px;
  z-index: 2;
  transform-origin: center center;
  font-family: 'Cormorant Garamond', serif;
  box-shadow: 0 4px 15px rgba(161, 140, 171, 0.1);
  font-style: normal;
  
  @media (max-width: 768px) {
    padding: 8px;
    max-width: 80px;
    font-size: 0.75rem;
  }
`;

const RainDrop = styled(motion.div)`
  position: absolute;
  color: #9b8b7a;
  font-size: 0.95rem;
  white-space: nowrap;
  z-index: 0;
  font-family: 'Cormorant Garamond', serif;
  font-style: normal;
  letter-spacing: 0.5px;
  opacity: 0.9;

  @media (max-width: 768px) {
    font-size: 0.85rem;
  }
`;

const Bloom = () => {
  const [encourageClicks, setEncourageClicks] = useState(0);
  const [showStem, setShowStem] = useState(false);
  const [showBlossom, setShowBlossom] = useState(false);
  const [stemWords, setStemWords] = useState([]);
  const [raindrops, setRaindrops] = useState([]);
  const [showText, setShowText] = useState(false);
  const [showSeed, setShowSeed] = useState(false);
  const [showEncourageButton, setShowEncourageButton] = useState(false);
  const [showGround, setShowGround] = useState(false);
  const containerRef = useRef(null);
  const groundRef = useRef(null);
  const seedRef = useRef(null);
  const [blossomPositionTop, setBlossomPositionTop] = useState(0);
  
  // Calculate responsive heights needed for rendering
  const isMobile = typeof window !== 'undefined' && window.innerWidth <= 768;
  const currentGroundHeight = isMobile ? 200 : 220;
  const currentStemHeight = isMobile ? 150 : 250;
  const stemStartPosition = isMobile ? 200 : 250;
  
  // Add Google Font link to head
  useEffect(() => {
    const link = document.createElement('link');
    link.rel = 'stylesheet';
    link.href = 'https://fonts.googleapis.com/css2?family=Cormorant+Garamond:ital,wght@0,300;0,400;0,600;1,300;1,400;1,600&display=swap';
    document.head.appendChild(link);
    
    return () => {
      document.head.removeChild(link);
    };
  }, []);
  
  // useEffect for initial animation sequence
  useEffect(() => {
    const groundTimer = setTimeout(() => {
      setShowGround(true);
    }, 100); // Very short delay before showing ground

    const textTimer = setTimeout(() => {
      setShowText(true);
    }, 600); // Delay after ground starts animating (100ms + 500ms)

    const seedTimer = setTimeout(() => {
      setShowSeed(true);
    }, 1600); // Delay after text starts fading in (600ms + 1000ms)

    const buttonTimer = setTimeout(() => {
      setShowEncourageButton(true);
    }, 2800); // Delay for seed drop (1600ms) + seed animation (1200ms)

    return () => {
      clearTimeout(groundTimer);
      clearTimeout(textTimer);
      clearTimeout(seedTimer);
      clearTimeout(buttonTimer);
    };
  }, []); // Run only once on mount
  
  const petalPhrases = [
    "of soul created to be carried with someone to make them feel more human",
  ];
  
  const stemFullLine = "And one day, it all comes together";
  const stemWordsArray = stemFullLine.split(' ');
  
  const rainPhrases = [
    "Just write a little bit",
    "Yes quit your job",
    "If everyone loved it it wouldn't mean anything",
    "It seems the more you don't want to share it, the better it is",
    "I made you dinner",
    "Yes it's weird but it's you",
    "How about a walk?",
    "I sent this to a friend",
    "Here's some tea",
  ];
  
  const handleEncourage = () => {
    const newClickCount = encourageClicks + 1;
    setEncourageClicks(newClickCount);

    const groundHeight = window.innerWidth <= 768 ? 200 : 220;
    
    let containerWidth = 0;
    if (containerRef.current) {
      containerWidth = containerRef.current.offsetWidth;
    } else {
      containerWidth = window.innerWidth;
    }

    // Define safe padding and estimate max text width
    const horizontalPadding = 20; 
    const estimatedMaxRaindropWidth = 120; // Reintroduce estimation

    // Calculate the initial range for the starting X position
    const minX = horizontalPadding;
    const maxX = Math.max(minX + 1, containerWidth - horizontalPadding); 

    // Calculate initial random x within the full padded range
    let potentialX = (maxX > minX) ? (Math.random() * (maxX - minX) + minX) : minX;

    // Calculate the maximum allowed starting X to prevent overflow
    const maxAllowedX = Math.max(minX, containerWidth - estimatedMaxRaindropWidth - horizontalPadding);

    // Clamp the potentialX if it exceeds the allowed maximum
    const finalX = Math.min(potentialX, maxAllowedX);

    const randomPhrase = rainPhrases[Math.floor(Math.random() * rainPhrases.length)];
    const newRaindrop = {
      id: uuidv4(),
      text: randomPhrase,
      x: finalX, // Use the clamped final X value
      startY: -20,
      endY: window.innerHeight - (groundHeight * (0.4 + Math.random() * 0.5)),
    };
    setRaindrops(prevDrops => [...prevDrops, newRaindrop]);

    if (newClickCount >= 5 && !showStem) {
      setShowStem(true);
      
      const wordSpacing = currentStemHeight / (stemWordsArray.length + 1);

      // Calculate and set the dynamic top position for the blossom
      let calculatedBlossomTop;
      const mobileBlossomRaiseOffset = 20; // Decreased from 40 to lower blossom slightly
      const desktopBlossomRaiseOffset = 80; // Increased from 50 to raise blossom on desktop

      if (isMobile) { // Use pre-calculated isMobile
        calculatedBlossomTop = window.innerHeight - currentGroundHeight - currentStemHeight - mobileBlossomRaiseOffset; // Use updated mobile offset
      } else {
        // Desktop calculation (raise slightly)
        calculatedBlossomTop = window.innerHeight - currentGroundHeight - currentStemHeight - desktopBlossomRaiseOffset;
      }
      setBlossomPositionTop(calculatedBlossomTop);
      
      const newStemWords = stemWordsArray.map((word, index) => {
        return {
          id: `stem-${index}`,
          text: word,
          y: window.innerHeight - stemStartPosition - (index + 1) * wordSpacing
        };
      });
      
      setStemWords(newStemWords);
      
      setTimeout(() => {
        setShowBlossom(true);
      }, 4500); // Increased delay to allow stem animation to finish
    }
  };

  return (
    <Container ref={containerRef}>
      <AnimatePresence>
        {showEncourageButton && !(isMobile && showStem) && (
          <motion.div
            initial={{ opacity: 0, y: -20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -20 }}
            transition={{ duration: 0.5 }}
            style={{ position: 'absolute', top: 20, right: 20, zIndex: 10 }} // Position wrapper
          >
            <EncourageButton onClick={handleEncourage}>Encourage Her</EncourageButton>
          </motion.div>
        )}
      </AnimatePresence>
      
      <AnimatePresence>
        {raindrops.map(drop => (
          <RainDrop
            key={drop.id}
            initial={{ left: drop.x, y: drop.startY, opacity: 0 }}
            animate={{ y: drop.endY, opacity: 0.9 }}
            exit={{ opacity: 0 }}
            transition={{ 
              duration: 6 + Math.random() * 4,
              ease: "easeOut",
              opacity: { duration: 0.8 }
            }}
          >
            {drop.text}
          </RainDrop>
        ))}
      </AnimatePresence>
      
      <motion.div
        ref={groundRef}
        initial={{ y: 220 }}
        animate={{ y: showGround ? 0 : 220 }}
        transition={{ duration: 0.8, ease: "easeOut" }}
        style={{
          position: 'absolute',
          bottom: 0,
          width: '100%',
        }}
      >
        <Ground>
          <GroundText>
            <motion.span
              initial={{ opacity: 0 }}
              animate={{ opacity: showText ? 1 : 0 }}
              transition={{ duration: 1.0 }}
            >
              Some part of me came alive the first time that you called me baby
            </motion.span>
          </GroundText>
          
          <Seed
            initial={{ opacity: 0, y: -160 }}
            animate={{
              opacity: showSeed ? 1 : 0,
              y: showSeed ? 0 : -160,
            }}
            transition={{
              duration: 1.2,
              delay: 0,
              ease: 'easeOut',
              opacity: { duration: 0.5 }
            }}
          >
            <div style={{ maxWidth: '100%', wordBreak: 'break-word' }}>
              I have all these things I've collected, that I am, that I want to share.
            </div>
          </Seed>
        </Ground>
      </motion.div>
      
      {showStem && (
        <>
          {/* <Stem
            initial={{ height: 0 }}
            animate={{ height: showStem ? currentStemHeight : 0 }}
            transition={{ duration: stemWordsArray.length * 0.5 + 0.5, ease: "easeOut" }} // Match duration to word animation
          /> */}
          <AnimatePresence>
            {stemWords.map((word, index) => (
              <StemWord
                key={word.id}
                initial={{ opacity: 0, y: word.y }}
                animate={{ opacity: 1 }}
                transition={{
                  duration: 1,
                  delay: index * 0.5,
                  ease: "easeOut"
                }}
              >
                {word.text}
              </StemWord>
            ))}
          </AnimatePresence>
        </>
      )}
      
      {showBlossom && (
        <BlossomContainer topPosition={blossomPositionTop}>
          <Blossom
            initial={{ scale: 0, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            transition={{ duration: 1.2, ease: "easeOut" }}
          >
            The Piece
          </Blossom>
          
          {petalPhrases.map((phrase, index) => {
            const words = phrase.split(' ');
            
            const startAngle = 0;
            
            const totalWords = petalPhrases.reduce((total, p) => total + p.split(' ').length, 0);
            const angleStep = 360 / totalWords;
            
            let offset = 0;
            for (let i = 0; i < index; i++) {
              offset += petalPhrases[i].split(' ').length;
            }
            
            return words.map((word, wordIndex) => {
              const angle = startAngle + ((offset + wordIndex) * angleStep);
              
              const radius = window.innerWidth <= 768 ? 90 : 110;
              
              const x = (Math.cos(angle * Math.PI / 180) * radius) - 30;
              const y = (Math.sin(angle * Math.PI / 180) * radius) - 20;
              
              const rotation = angle;
              
              return (
                <Petal
                  key={`${index}-${wordIndex}`}
                  initial={{ x: 0, y: 0, scale: 0, rotate: 0, opacity: 0 }}
                  animate={{ 
                    x, 
                    y, 
                    scale: 1,
                    rotate: rotation,
                    opacity: 0.95
                  }}
                  transition={{ 
                    duration: 1.5, 
                    delay: (index * 0.6) + (wordIndex * 0.4),
                    ease: "easeOut"
                  }}
                  style={{
                    position: 'absolute',
                    left: '50%',
                    top: '50%',
                    transform: `translate(${x}px, ${y}px) rotate(${rotation}deg)`
                  }}
                >
                  {word}
                </Petal>
              );
            });
          })}
        </BlossomContainer>
      )}
    </Container>
  );
};

export default Bloom;
